/* eslint-disable @typescript-eslint/return-await */
/* eslint-disable @typescript-eslint/no-magic-numbers */
/**
 * Worker Manager
 * Handles communication with web workers and manages their lifecycle
 */

import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import {
  CURR_ENV,
  STATIC_API_BASE_URL,
} from '@/core/constants/appConfig/environment';
import { EScriptEnv } from '@/core/constants/appConfig/types';
import type { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { StorageHandler } from '@/core/data_handlers/storage_handler';
import { retreiveAndStoreFlag } from '@/core/event_logic/utils/flag_utils';
import { SHA256 } from '@/core/event_logic/utils/hash_utils';
import { addEntryToRetryInfo } from '@/core/event_logic/utils/mable_event_utils';
import { Logger } from '@/shared/logger/logger';

import { BaseWorkerResponse } from './shared/worker_utils';

/**
 * Task sent to a worker
 */
export type WorkerTask = {
  createdAt: number;
  errors: string[];
  id: string;
  origin?: string;
  payload: any;
  retryCount: number;
  state: 'completed' | 'pending' | 'processing';
  type: string;
};

/**
 * Response received from a worker
 */
export type WorkerResponse = {
  data?: any;
  error?: string;
  id: string;
  origin?: string;
  success: boolean;
  type: string;
};

/**
 * Options for WorkerManager
 */
export type WorkerManagerOptions = {
  /**
   * Bundle name from which the worker is called
   * Used for proper labeling in worker logs
   */
  bundleName?: string;

  /**
   * Whether to disable logging
   */
  disableLogs?: boolean;

  /**
   * Always fetch worker script live
   */
  disableWorkerCache?: boolean;

  /**
   * Main thread bundle name
   * When provided, will be combined with worker's bundle name for logging
   */
  mainThreadBundleName?: string;

  /**
   * Unique origin identifier to track tasks from this instance
   */
  origin?: string;

  /**
   * Determines whether a worker should retry tasks with no response / error
   */
  retryEnabled?: boolean;
};

type WorkerTaskQueue = Record<string, WorkerTask>;
type WorkerTaskCallback = (error: Error | null, data?: any) => void;

const WORKER_TASK_MAX_RETRY_LIMIT = 3;
const WORKER_DEFAULT_CACHE = 30 * 60 * 1000; // 30 mins
const WORKER_RETRY_MS = 50; // 50ms
const WORKER_QUEUE_MAX_TASKS = 150; // 10KB * 150 => 150KB max queue size

/**
 * WorkerManager class for handling worker communication
 */
export class WorkerManager {
  private worker: Worker | null = null;
  private readonly messageChannel: MessageChannel;
  private taskQueue: WorkerTaskQueue;
  private readonly _storageHandler: StorageHandler<WorkerTaskQueue>;
  private readonly pendingTasks = new Map<
    string,
    // eslint-disable-next-line @typescript-eslint/ban-types
    { reject: Function; resolve: Function }
  >();
  private readonly workerUrl: string;
  private initialized = false;
  private usingBlobUrl = false;
  private blobUrl: string | null = null;
  private readonly disableLogs: boolean;
  private readonly origin: string;
  private readonly bundleName?: string;
  private readonly mainThreadBundleName?: string;
  private readonly _workerInitPromise: Promise<Worker>;
  private readonly disableWorkerCache: boolean;
  private readonly retryEnabled: boolean;
  private workerInitTime = 0;

  /**
   * Creates a new worker manager
   * @param workerPath - Path to the worker script (can be filename, relative path, or full URL)
   * @param options - Configuration options for the worker manager
   */
  constructor(workerPath: string, options?: WorkerManagerOptions) {
    this.disableLogs = options?.disableLogs || false;
    this.retryEnabled = options?.retryEnabled || false;

    // Include environment in origin to prevent cross-environment communication
    const envPrefix = `env_${CURR_ENV}_`;

    this.origin = `${envPrefix}${workerPath}_mgr`;

    const disableWorkerCacheFlag = retreiveAndStoreFlag(
      'disable_worker_cache',
      EBrowserStorageType.LOCALSTORAGE,
    );
    this.disableWorkerCache =
      options?.disableWorkerCache || disableWorkerCacheFlag === '1';

    // Store bundle name if provided
    this.bundleName = options?.bundleName
      ? options.bundleName.toLowerCase()
      : undefined;

    this.taskQueue = {};
    this._storageHandler = new StorageHandler(
      `${CURR_ENV}_${Logger.bundleName}_${workerPath}_worker_task_queue`,
      {},
      EBrowserStorageType.LOCALSTORAGE,
    );

    this.messageChannel = new MessageChannel();
    // eslint-disable-next-line unicorn/prefer-add-event-listener
    this.messageChannel.port1.onmessage =
      this.handleMessageFromWorker.bind(this);
    this.messageChannel.port1.start();

    // Store main thread bundle name if provided
    this.mainThreadBundleName = options?.mainThreadBundleName;

    // Check if the path is already a full URL
    if (workerPath.startsWith('http')) {
      this.workerUrl = workerPath;
    } else {
      // Extract filename if a path is provided
      const fileName = workerPath.split('/').pop() || workerPath;

      // Use dist folder in local or test environment, otherwise use static2
      if (CURR_ENV === EScriptEnv.TEST || CURR_ENV === EScriptEnv.LOCAL) {
        // Ensure the URL doesn't have a trailing slash
        const baseUrl = STATIC_API_BASE_URL.endsWith('/')
          ? STATIC_API_BASE_URL.slice(0, -1)
          : STATIC_API_BASE_URL;

        this.workerUrl = `${baseUrl}/dist/${fileName}`;
      } else {
        // For production and other environments, use static2 path
        // Ensure the URL doesn't have a trailing slash
        const baseUrl = STATIC_API_BASE_URL.endsWith('/')
          ? STATIC_API_BASE_URL.slice(0, -1)
          : STATIC_API_BASE_URL;

        this.workerUrl = `${baseUrl}/static2/${fileName}`;
      }
    }

    if (!this.disableLogs) {
      Logger.debug(`Worker URL set to: ${this.workerUrl}`);
      Logger.debug(`Worker origin ID: ${this.origin}`);
    }

    this.taskQueue = {};
    this._workerInitPromise = this.initWorker();

    // Retry logic starts here - fetch previous tasks
    if (!this.retryEnabled) return;

    const prevQueue = this._storageHandler.get();
    // sort in latest tasks order
    const prevQueueKeys = Object.keys(prevQueue)
      .sort(
        (a, b) =>
          (prevQueue[b]?.createdAt || 0) - (prevQueue[a]?.createdAt || 0),
      )
      .slice(0, WORKER_QUEUE_MAX_TASKS);

    Logger.debug(this.workerUrl, 'cached task ids', {
      storedTaskIDs: prevQueueKeys,
    });

    for (let taskId of prevQueueKeys) {
      // process existing events.
      const storedTask = prevQueue[taskId];
      if (!storedTask) return;

      if (storedTask.state !== 'completed') {
        storedTask.state = 'pending';
        storedTask.origin = this.origin;
        storedTask.retryCount++;
        storedTask.errors.push('no response from worker');
      }

      if (storedTask.type === 'SEND_MABLE_EVENT') {
        storedTask.payload.mableEvent = addEntryToRetryInfo(
          {
            layer: 'worker',
            reason: `Retrying task due to no response from worker, retry attempt: ${storedTask.retryCount}`,
            timestamp: Date.now(),
          },
          storedTask.payload.mableEvent,
        );
      }

      void this.addTaskToQueue(storedTask);
    }
  }

  private async handleMessageFromWorker(
    event: MessageEvent<BaseWorkerResponse>,
  ) {
    const { data, error, id, success, type } = event.data;
    Logger.info(
      `[WorkerManager:${this.origin}] Response from worker: ${id} [${type}] - ${Logger.getTimeSinceScriptStart()}ms since init`,
      event.data,
    );
    const pendingTask = this.pendingTasks.get(id);
    if (!pendingTask) {
      if (!this.disableLogs) {
        Logger.debug(
          `[WorkerManager:${this.origin}] No pending task found for response ID: ${id}, ignoring response`,
        );
      }
      return;
    }

    if (success) {
      this.pendingTasks.delete(id);
      pendingTask.resolve(data);
    } else {
      pendingTask.reject(new Error(error));
    }
  }

  async generateTaskId(taskType: string, taskPayload: any) {
    const dedupKey = taskPayload.dedupKey;
    if (dedupKey) return taskType + '_' + dedupKey;
    const payloadStr = taskType + '_' + JSON.stringify(taskPayload);
    return await SHA256(payloadStr);
  }

  private async getWorker(): Promise<Worker> {
    // ensure only one worker is atmost initialized
    if (this.worker) return this.worker;
    return await this._workerInitPromise;
  }

  /**
   * Create a worker using Blob URL to bypass CORS restrictions
   * @param url - The URL of the worker script
   * @returns A promise that resolves to a Worker instance
   */
  private async createWorkerFromBlob(url: string): Promise<Worker> {
    try {
      if (!this.disableLogs) {
        Logger.info(`Fetching worker script from ${url} to create blob URL`);
      }

      const now = Date.now();
      const currKey = `${this.origin}-workerScript`;
      const cacheValue = localStorage.getItem(currKey);
      const cachedScript: { lastUpdated: number; scriptText: string } | null =
        cacheValue ? JSON.parse(cacheValue) : null;

      let scriptText: string;

      if (
        !cachedScript ||
        now - cachedScript.lastUpdated >= WORKER_DEFAULT_CACHE ||
        this.disableWorkerCache
      ) {
        const response = await fetch(url, {
          credentials: 'omit',
          headers: {
            Accept: 'application/javascript',
          },
          keepalive: true,
          // we can't use no-cors here because we need to access the worker script!!
        });
        if (!response.ok)
          throw new Error(
            `Failed to fetch worker script: ${response.status} ${response.statusText} from URL: ${url}`,
          );
        scriptText = await response.text();
        localStorage.setItem(
          currKey,
          JSON.stringify({ lastUpdated: Date.now(), scriptText }),
        );
      } else scriptText = cachedScript.scriptText;

      // Create a blob URL from the worker code
      const blob = new Blob([scriptText], { type: 'application/javascript' });
      this.blobUrl = URL.createObjectURL(blob);
      this.usingBlobUrl = true;

      if (!this.disableLogs) {
        Logger.info(`Created blob URL for worker with environment ${CURR_ENV}`);
      }

      return new Worker(this.blobUrl);
    } catch (error) {
      if (!this.disableLogs) {
        Logger.error(`Failed to create worker from blob:`, error);
        Logger.error(`Worker blob creation details:`, {
          bundleName: this.bundleName || 'unknown',
          environment: CURR_ENV,
          mainThreadBundleName: this.mainThreadBundleName || 'unknown',
          origin: this.origin,
          url,
        });
      }

      // Attach worker details to the error object for better error handling upstream
      const workerDetails = {
        blobUrl: null,
        bundleName: this.bundleName || 'unknown',
        environment: CURR_ENV,
        fetchMethod: 'blob',
        mainThreadBundleName: this.mainThreadBundleName || 'unknown',
        origin: this.origin,
        usingBlobUrl: true,
        workerUrl: url,
      };

      if (error instanceof Error) {
        (error as any).workerDetails = workerDetails;
      }

      throw error;
    }
  }

  /**
   * Initialize the worker if not already initialized
   * @returns The worker instance
   */
  private async initWorker(): Promise<Worker> {
    if (!this.worker) {
      try {
        if (!this.disableLogs) {
          Logger.info(`Initializing worker from ${this.workerUrl}`);
        }

        // Append environment to the URL if not already present
        let workerUrl = this.workerUrl;

        // Add env parameter
        if (!workerUrl.includes('env=') && !workerUrl.includes('?')) {
          workerUrl += `?env=${CURR_ENV}`;
        } else if (!workerUrl.includes('env=') && workerUrl.includes('?')) {
          workerUrl += `&env=${CURR_ENV}`;
        }

        // Add bundle name parameter if available
        if (this.bundleName && !workerUrl.includes('bundle=')) {
          workerUrl += `&bundle=${this.bundleName}`;
        }

        // Add main thread bundle name parameter if available
        if (this.mainThreadBundleName && !workerUrl.includes('mainBundle=')) {
          workerUrl += `&mainBundle=${this.mainThreadBundleName}`;
        }

        // Add origin parameter
        if (!workerUrl.includes('origin=')) {
          workerUrl += `&origin=${this.origin}`;
        }

        // Removed cache busting parameter to optimize loading and leverage browser caching
        // Workers are typically static and should be cached by the browser for better performance

        if (!this.disableLogs) {
          Logger.debug(`Attempting to load worker from: ${workerUrl}`);
        }

        // Flag to track if we should try the blob approach immediately
        const useBlobFirst =
          // If we're in a non-local environment or the URL is external
          (CURR_ENV !== EScriptEnv.LOCAL && !workerUrl.startsWith('/')) ||
          // Or if we've detected CORS issues previously in the same session
          document.cookie.includes('worker_use_blob=true');

        if (useBlobFirst) {
          if (!this.disableLogs) {
            Logger.info(
              'Using blob approach directly due to previous CORS issues or non-local environment',
            );
          }
          this.worker = await this.createWorkerFromBlob(workerUrl);
        } else {
          try {
            // Try direct initialization
            this.worker = new Worker(workerUrl);
          } catch (directError) {
            // If error contains CORS or network-related issues, try the blob approach
            if (
              directError instanceof DOMException &&
              (directError.name === 'SecurityError' ||
                directError.name === 'NetworkError' ||
                directError.message.includes('origin'))
            ) {
              if (!this.disableLogs) {
                Logger.warn(
                  `Direct worker initialization failed with CORS error, trying blob URL approach: ${directError.message}`,
                );
              }

              // Set a cookie to remember to use blob approach next time
              // eslint-disable-next-line unicorn/no-document-cookie
              document.cookie = 'worker_use_blob=true; path=/; max-age=3600';

              this.worker = await this.createWorkerFromBlob(workerUrl);
            } else {
              // Re-throw if it's not a CORS issue
              throw directError;
            }
          }
        }

        this.worker.addEventListener('error', error => {
          if (!this.disableLogs) {
            Logger.error('Worker error:', error);
            Logger.error('Worker error details:', {
              blobUrl: this.blobUrl || 'not_used',
              bundleName: this.bundleName || 'unknown',
              environment: CURR_ENV,
              errorDetails: {
                colno: error.colno,
                filename: error.filename,
                lineno: error.lineno,
                message: error.message,
              },
              origin: this.origin,
              usingBlobUrl: this.usingBlobUrl,
              workerUrl: this.workerUrl,
            });
          }
        });

        // Wait for worker to initialize
        await new Promise<void>(resolve => {
          setTimeout(resolve, 0);
        });

        this.initialized = true;
      } catch (error) {
        if (!this.disableLogs) {
          Logger.error('Failed to initialize worker:', error);
          Logger.error('Worker initialization details:', {
            blobUrl: this.blobUrl || 'not_used',
            bundleName: this.bundleName || 'unknown',
            environment: CURR_ENV,
            mainThreadBundleName: this.mainThreadBundleName || 'unknown',
            origin: this.origin,
            usingBlobUrl: this.usingBlobUrl,
            workerUrl: this.workerUrl,
          });
        }

        // Attach worker details to the error object for better error handling upstream
        const workerDetails = {
          blobUrl: this.blobUrl || 'not_used',
          bundleName: this.bundleName || 'unknown',
          environment: CURR_ENV,
          mainThreadBundleName: this.mainThreadBundleName || 'unknown',
          origin: this.origin,
          usingBlobUrl: this.usingBlobUrl,
          workerUrl: this.workerUrl,
        };

        if (error instanceof Error) {
          (error as any).workerDetails = workerDetails;
        }

        throw error;
      }
    }
    if (this.worker) {
      this.worker.postMessage(
        { port: this.messageChannel.port2, type: 'SETUP_CHANNEL' },
        [this.messageChannel.port2],
      );
      this.workerInitTime = Logger.getTimeSinceScriptStart();
    }
    return this.worker;
  }

  /**
   * Execute a task in the worker and return a promise that resolves with the result
   * @param type - Task type
   * @param payload - Task payload
   * @returns Promise that resolves with the worker's response
   */
  public async executeTask(type: string, payload: any): Promise<any> {
    await this.getWorker();
    try {
      const taskId = await this.generateTaskId(type, payload);

      if (!this.disableLogs) {
        Logger.info(`[WorkerManager:${this.origin}] Executing task ${type}:`, {
          payload,
          taskId,
        });
      }

      return new Promise((resolve, reject) => {
        this.pendingTasks.set(taskId, { reject, resolve });

        const task: WorkerTask = {
          createdAt: Date.now(),
          errors: [],
          id: taskId,
          origin: this.origin, // Add origin to identify tasks from this instance
          payload,
          retryCount: 0,
          state: 'pending',
          type,
        };

        if (!this.disableLogs) {
          Logger.debug(
            `[WorkerManager:${this.origin}] Sending task to worker:`,
            {
              id: taskId,
              origin: this.origin,
              type,
            },
          );
        }

        // eslint-disable-next-line unicorn/require-post-message-target-origin
        this.messageChannel.port1.postMessage(task);
      });
    } catch (error) {
      if (!this.disableLogs) {
        Logger.error(
          `[WorkerManager:${this.origin}] Task execution failed for ${type}:`,
          error,
          {
            blobUrl: this.blobUrl,
            bundleName: this.bundleName || 'unknown',
            usingBlobUrl: this.usingBlobUrl,
            workerUrl: this.workerUrl,
          },
        );
      }

      // Attach worker details to the error object for better error handling upstream
      const workerDetails = {
        blobUrl: this.blobUrl,
        bundleName: this.bundleName || 'unknown',
        environment: CURR_ENV,
        origin: this.origin,
        usingBlobUrl: this.usingBlobUrl,
        workerUrl: this.workerUrl,
      };

      if (error instanceof Error) {
        (error as any).workerDetails = workerDetails;
      }

      throw error;
    }
  }

  /**
   * Directly add task to queue
   */
  private async addTaskToQueue(
    task: WorkerTask,
    onComplete?: WorkerTaskCallback,
  ) {
    if (task.retryCount && !this.retryEnabled) {
      Logger.info('[WorkerManager] Worker retry is disabled');
      delete this.taskQueue[task.id];
      return;
    }
    if (task.retryCount >= WORKER_TASK_MAX_RETRY_LIMIT) {
      this.pendingTasks.delete(task.id);
      delete this.taskQueue[task.id];
      this.saveQueue();
      Logger.info(
        '[workermgr] Removing task due to max retries / disable worker retry: ' +
          task.type +
          ', ID: ' +
          task.id,
      );
      return;
    }

    // already another instance processing
    if (this.taskQueue[task.id]?.state === 'processing') return;

    // wait for worker to init
    await this.getWorker();

    try {
      this.taskQueue[task.id] = task;
      if (task.state === 'completed') return;
      task.state = 'processing';
      this.saveQueue();

      if (task.type === 'SEND_MABLE_EVENT') {
        task.payload.mableEvent = addEntryToRetryInfo(
          {
            layer: 'worker',
            reason: `entered worker queue`,
            timestamp: Date.now(),
          },
          task.payload.mableEvent,
        );
      }

      if (!this.disableLogs) {
        Logger.info(
          `[WorkerManager:${this.origin}] Submitting task ${task.type}:`,
          {
            id: task.id,
            payload: task.payload,
          },
        );
      }

      if (task.type === 'SEND_MABLE_EVENT') {
        // inject worker init time into the payload
        const mableEvent = task.payload.mableEvent as TMableEvent;
        mableEvent.uaHeaders = {
          ...mableEvent.uaHeaders,
          'worker-init-time': this.workerInitTime.toFixed(2),
          'worker-retry-count': task.retryCount.toString(),
          'worker-retry-errors': JSON.stringify(task.errors),
        };
        task.payload.mableEvent = mableEvent;
      }

      const errCallback = (error: Error) => {
        // add it back to the queue
        const taskID = task.id;
        const curr = this.taskQueue[taskID];
        if (
          !curr ||
          curr.retryCount >= WORKER_TASK_MAX_RETRY_LIMIT ||
          !this.retryEnabled
        )
          return;
        if (curr.type === 'SEND_MABLE_EVENT') {
          curr.payload.mableEvent = addEntryToRetryInfo(
            {
              layer: 'worker',
              reason: `Error: ${error.message}`,
              timestamp: Date.now(),
            },
            curr.payload.mableEvent,
          );
        }
        curr.state = 'pending';
        setTimeout(
          () => this.addTaskToQueue(curr, onComplete),
          WORKER_RETRY_MS,
        );
        this._storageHandler.set(this.taskQueue);
        if (onComplete) onComplete(error, null);
      };

      const finishCallback = (data: any) => {
        // remove from queue
        const taskId = task.id;
        if (this.taskQueue[taskId]) this.taskQueue[taskId].state = 'completed';
        this._storageHandler.set(this.taskQueue);
        if (onComplete) onComplete(null, data);
        Logger.info('[worker] Completed task with ID: ', task.id);
      };

      this.pendingTasks.set(task.id, {
        reject: errCallback,
        resolve: finishCallback,
      });

      if (!this.disableLogs) {
        Logger.info(
          `[WorkerManager:${this.origin}] Sending background task to worker:`,
          {
            id: task.id,
            origin: this.origin,
            type: task.type,
          },
        );
      }

      // eslint-disable-next-line unicorn/require-post-message-target-origin
      this.messageChannel.port1.postMessage(task);
    } catch (error) {
      Logger.info('[worker] failed to send message to worker', task.id);
      if (!this.disableLogs) {
        Logger.error(
          `[WorkerManager:${this.origin}] Task submission failed for ${task.type}:`,
          error,
          {
            blobUrl: this.blobUrl,
            bundleName: this.bundleName || 'unknown',
            usingBlobUrl: this.usingBlobUrl,
            workerUrl: this.workerUrl,
          },
        );
      }

      throw error;
    }
  }

  private saveQueue() {
    if (!this.retryEnabled) return;
    this._storageHandler.set(this.taskQueue);
  }

  /**
   * Submit a task to the worker without waiting for the result
   * @param type - Task type
   * @param payload - Task payload
   * @param onComplete - Optional callback function to handle the result when it completes
   * @returns The task ID that can be used to track the task
   */
  public async submitTask(
    type: string,
    payload: any,

    onComplete?: WorkerTaskCallback,
  ): Promise<string> {
    const taskId = await this.generateTaskId(type, payload);
    // skip payload already processed
    if (this.taskQueue[taskId]) return '';

    const task: WorkerTask = {
      createdAt: Date.now(),
      errors: [],
      id: taskId,
      origin: this.origin,
      payload,
      retryCount: 0,
      state: 'pending',
      type,
    };

    void this.addTaskToQueue(task, onComplete);

    return task.id;
  }

  /**
   * Check if the worker is initialized
   * @returns True if the worker is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Terminate the worker
   */
  public terminate(): void {
    if (this.worker) {
      if (!this.disableLogs) {
        Logger.info('Terminating worker');
      }
      this.worker.terminate();
      this.worker = null;
      this.initialized = false;

      // Release blob URL if it was created
      if (this.usingBlobUrl && this.blobUrl) {
        try {
          URL.revokeObjectURL(this.blobUrl);
          if (!this.disableLogs) {
            Logger.info('Revoked blob URL');
          }
          this.blobUrl = null;
        } catch (error) {
          // Ignore errors when trying to revoke URL
          if (!this.disableLogs) {
            Logger.warn('Failed to revoke blob URL', error);
          }
        }
      }

      this.usingBlobUrl = false;
    }
  }
}
